import React, { Component } from 'react'

import { graphql } from '@apollo/client/react/hoc'
import { Container, Row, Col } from 'reactstrap'
import CarsList from '../CarsList'
import ReturnsList from '../ReturnsList'
import '../CarsList.css'
import { TagsQuery } from "../../Functions/querys"
import AvailableCarsHeader from "./AvailableCarsHeader"
import ListingTabs from "../../Material/ListingTabs"
import { buildPresets } from "../../Filters/presetFilters"
import { FILTER_PARAM } from "../../Filters/filterUrl"

import withApolloClient from '../../withApolloClient'

// Available Cars' two defaults, expressed as Advanced Filters presets -- the
// same FilterInput shape the query already sends for user filters, so the
// popover, the chips and the query all read one source of truth.
//
// filterValues is the string "true", not a boolean: BOOLEAN_OPTIONS in
// AdvancedFilters.js is [{ value: "true", label: "Yes" }, ...], and both the
// chip label and the popover's value select match against those string values.
// A real boolean matches neither, so the chip renders blank (React drops
// boolean children) and the select shows nothing selected.
//
// Is Ready is editable -- users could toggle it on the legacy bar, so they
// still can. Is Available is locked: it replaces the stage=[9] argument this
// page used to hardcode on the query, which is what makes this page Available
// Cars rather than Fleet Management, so it shows as a chip with a lock icon
// and cannot be edited or removed.
//
// It is is_available, not `stage equal 9`: resolve_all_cars special-cases the
// legacy argument, swapping the queryset for the Car.available manager (stage
// 9 *plus* not hidden, at a pickup location, plated, unreserved and free of
// blocking issues) instead of filtering stage__in. A plain stage filter would
// quietly widen the page. See _is_available_descriptor in cars/filters.py.
const AVAILABLE_CARS_PRESET = buildPresets([
    { fieldName: "is_ready", filterType: "equal", filterValues: "true", editable: true },
    { fieldName: "is_available", filterType: "equal", filterValues: "true", editable: false },
])
// Scheduled Returns replaces the two values this page used to hardcode on the
// query: returnStage [2] and agreementStage 10.
//
// Both values are strings, not ints: enum options are advertised as strings
// (FilterFieldOptionType.value is a graphene.String()), and that is what the
// chip label and the popover select look values up by. See sameOptionValue in
// Filters/AdvancedFilters.js -- the backend coerces either form, so an int
// would still filter correctly, it would just render as a bare "2".
//
// Return Stage is constrained rather than freely editable: this tab is about
// returns that are scheduled, so the user may pick Created (1) and/or Confirmed
// (2) and nothing else, and cannot remove or empty the filter. Agreement Stage
// is locked outright: the page is about scheduled returns on active rentals, so
// it shows as a chip with a lock icon and cannot be edited or removed.
const SCHEDULED_RETURNS_PRESET = buildPresets([
    { fieldName: "stage", filterType: "in", filterValues: ["2"], editable: true, choices: ["1", "2"] },
    { fieldName: "agreement_stage", filterType: "equal", filterValues: "10", editable: false },
])

const tabs = [
    { id: "#avaialble", name: "Available Cars", filterValues: { orderBy: ["-id"] }, presets: AVAILABLE_CARS_PRESET },
    { id: "#scheduled", name: "Scheduled Returns", filterValues: { orderBy: ["-id"] }, presets: SCHEDULED_RETURNS_PRESET },
]

class AvailableCarsContainer extends Component {

    constructor(props) {
        super(props)
        this.state = {
            activeTab: "Available Cars",
            selectedIds: [],
            filterValues: {
                orderBy: ["-id"]
            },
            // presets and defaultPresets start out equal -- setPresets only ever
            // updates presets, so defaultPresets stays the active tab's declared
            // set and is what Reset returns to.
            presets: AVAILABLE_CARS_PRESET,
            defaultPresets: AVAILABLE_CARS_PRESET,
        }
    }

    updateOrder = (string) => {
        let order = this.state.filterValues && this.state.filterValues.orderBy ? [...this.state.filterValues.orderBy] : []
        let index = order.indexOf(string)
        if (index > -1) {
            order[index] = "-" + string
        } else {
            index = order.indexOf("-" + string)
            index > -1 ? order.splice(index, 1) : order.unshift(string)
        }
        this.setState({ filterValues: { ...this.state.filterValues, orderBy: order } })
    };

    updateSelection = (selection) => {
        if (Array.isArray(selection)) {
            this.setState({ selectedIds: selection })
        } else {
            if (this.state.selectedIds.includes(selection))
                this.setState({ selectedIds: this.state.selectedIds.filter(item => item != selection) })
            else
                this.setState({ selectedIds: [...this.state.selectedIds, selection] })
        }
    }
    setPresets = (presets) => this.setState({ presets })

    // Writes the tab hash through navigate() (path + search + hash together) instead of
    // `window.location.hash = …`. Two problems with the direct write: react-router's own
    // location.hash (read by useLocation() everywhere, including Filters/filterUrl.js) went
    // stale, so applying a filter right after a tab switch could silently revert the URL to
    // the previous tab's hash; and it never touched `?filter=`. Available Cars (entity `car`)
    // and Scheduled Returns (entity `car_return`) both read that one shared query param
    // through useAdvancedFilters() -- CarsList and ReturnsList respectively -- so without
    // clearing it here, filters applied on one tab leak into the other tab's GraphQL
    // `filters` variable (car-only fields like is_ready/tag_name even error out against
    // CarReturnFilterSet). Same fix as Material/TabbedListingPage.js's writeTabHash.
    navigateToTab = (tabIndex, { replace = false } = {}) => {
        const params = new URLSearchParams(this.props.location.search)
        params.delete(FILTER_PARAM)
        this.props.navigate(
            { pathname: this.props.location.pathname, search: params.toString(), hash: tabs[tabIndex].id },
            { replace }
        )
    }

    componentDidMount() {
        if (window.location.hash && tabs.map(item => item.id).includes(window.location.hash)) {
            let tabIndex = tabs.findIndex(tab => tab.id === window.location.hash)
            this.setState({
                filterValues: { ...tabs[tabIndex].filterValues },
                activeTab: tabs[tabIndex].name,
                presets: tabs[tabIndex].presets,
                defaultPresets: tabs[tabIndex].presets,
            })
            this.navigateToTab(tabIndex, { replace: true })
        } else {
            this.setState({
                filterValues: { ...tabs[0].filterValues },
                activeTab: tabs[0].name,
                presets: tabs[0].presets,
                defaultPresets: tabs[0].presets,
            })
            this.navigateToTab(0, { replace: true })
        }
    }
    changeTab = (value) => {
        let tabIndex = tabs.findIndex(tab => tab.name === value)
        this.setState({
            activeTab: value,
            filterValues: { ...tabs[tabIndex].filterValues },
            presets: tabs[tabIndex].presets,
            defaultPresets: tabs[tabIndex].presets,
        })
        this.navigateToTab(tabIndex)
    }

    render() {
        return (

            < Container fluid className="AvailableCarsContainer CarListContainer" >
                <ListingTabs
                    tabs={[
                        { ...tabs[0], iconClassName: "fa fa-car" },
                        { ...tabs[1], iconClassName: "fa fa-calendar-o" },
                    ]}
                    activeTab={this.state.activeTab}
                    activeValueKey="name"
                    onChange={this.changeTab}
                />
                <AvailableCarsHeader
                    activeTab={this.state.activeTab}
                    filterValues={this.state.filterValues}
                    setFilterValues={(filterValues) => this.setState({ filterValues })}
                    selectedIds={this.state.selectedIds}
                    location={this.props.location} history={this.props.navigate}
                    resetSelectedIds={() => this.setState({ selectedIds: [] })}
                    presets={this.state.presets}
                    setPresets={this.setPresets}
                    defaultPresets={this.state.defaultPresets}
                />
                <Row>
                    <Col sm="12">
                        {this.state.activeTab && this.state.activeTab === "Available Cars" &&
                            <CarsList
                                currentUser={this.props.currentUser}
                                searchTerm={this.state.filterValues && this.state.filterValues.searchTerm !== null ? this.state.filterValues.searchTerm : null}
                                orderBy={this.state.filterValues && this.state.filterValues.orderBy ? this.state.filterValues.orderBy : null}
                                updateOrder={this.updateOrder}
                                presets={this.state.presets}
                            />
                        }
                        {this.state.activeTab && this.state.activeTab === "Scheduled Returns" &&
                            <ReturnsList
                                currentUser={this.props.currentUser}
                                searchTerm={this.state.filterValues && this.state.filterValues.searchTerm !== null ? this.state.filterValues.searchTerm : null}
                                activeTab="Available Cars"
                                selectedDrivers={this.state.selectedIds}
                                updateDriversSelection={this.updateSelection}
                                orderBy={this.state.filterValues && this.state.filterValues.orderBy ? this.state.filterValues.orderBy : null}
                                updateOrder={this.updateOrder}
                                presets={this.state.presets}
                            />
                        }
                    </Col>
                </Row>
            </Container >
        )
    }
}


//-----------------------------------
export default graphql(TagsQuery, {
    props: ({ data: { loading, carTags } }) => ({
        carTags
    })
})(withApolloClient(AvailableCarsContainer))
